import BaseService from '../../base/base_service';
import Event from '../../utils/event';
import DOM from '../../utils/dom';
import {SYSTEM_EVENTS} from '../../events/events';
import {ON_DEMAND} from '../hybrid/native/native_const';
import GameServiceDependenciesFactory from './dependencies/game_service_dependencies_factory';
import VoidGamePostMessageListener from './post_messages/void_game_post_message_listener';
import Device from '../../utils/device';
import Store from '../../utils/store';
import _ from '../../utils/underscore';
import {CGP_ERROR_CODES} from '../../exceptions/cgp_error_codes';
import BaseObject from '../../base/base_object';
import RestrictionMessages from '../restrictions/messages/restriction_messages';
import VoidGamePostMessageHandler from './post_messages/void_game_post_message_handler';
import TimeSpan from '../../utils/time_span';
import {isCancelGameFlowResponse} from "./game_steps/game_step_base";
import {imagePreloadWithDefault} from "../../../../common/utils/image-preload";


/* Definition of the gameOptions.
 * Below you can find all the possible fields that can be found in the gameOptions structure
 * as parameter of the open game flows
 *
 * {
           gameType,
           gameName,
           gameInfo,
           gameRoundId,
           skipFreePlay,
           marketing,
           inGameModeSwitching,
           showSwitchGameMode,
           navbar: {
               login: {
                   showRegister
               }
           },
           referrerId,
           freePlayData: {
               freePlayId,
               freePlayType
           },
           responsibleGamblingView,
           token,
           gameSize: {
               width,
               height
           },
           isResizable,
           isAnonymousSupported,
           realityCheckInterval,
           raceId,
           grsData: {
               flashUrl,
               wrapperUrl,
               html5Url,
               chillUrl,
               abTestingParams: {
                   TestCaseId,
                   TestId
               },
               abTestingData
           },
           openSession: {
               SessionID
           },
           italy: {
                gameLimits
           },
           spain: {
               gameLimits: {
                   RequestedGameLimit,
                   RequestedTimeLimit,
                   RestrictionPeriod,
                   IntervalReminderInMinutes
               }
           }
           launchGameUrl,
           postMessageHandler,
           gameUrlBuilder,
           gameNavigationBar,
           gameProviderCssClassName
           gameBody,
           gameRegulationStripe,
           gameRegulationStripeUpdater,
           gameWindow,
           gameSessionSummaryHandler,
           isOpenedInSecondWebView,
           company,
           sessionTimer,
           forceOpenGameInRedirect,
           useNetEntGameInclusion,
           useEvolutionNewIntegration,
           useAdapterIntegration
      }
 */

class VoidLoadingIndicator extends BaseObject {
    showLoadingIndicator() {

    }

    closeLoadingIndicator() {

    }
}

export default class GameService extends BaseService {
    static CLASS = 'GameService';

    init(params) {
        super.init(params);
        this._dependenciesFactory = params.dependenciesFactory || GameServiceDependenciesFactory.create({
            getServiceFactoryCallback: () => this.serviceFactory
        });

        this._openRealGameApiUrl = params.openRealGameApiUrl;
        this._openAnonymousGameApiUrl = params.openAnonymousGameApiUrl;
        this._openHistoryGameApiUrl = params.openHistoryGameApiUrl;
        this._getGameSessionDataUrl = params.getGameSessionDataUrl;
        this._getGRSEnvironmentsUrl = params.getGRSEnvironmentsUrl;

        this._chillEnvironment = params.ChillEnvironment;

        this._backgroundDefault = params.backgroundDefault;
        this._gameDefaultIcon = params.gameDefaultIcon;
        this._customGamesSettings = params.customGamesSettings || {};
        this._resolveGameImagePath = params.resolveGameImagePath;

        this._affiliatesGoToLobbyInterval = this._setAffiliatesGoToLobbyInterval(params.AffiliatesGoToLobbyInterval);

        if(params.GRS) {
            this._customChillUrl = params.GRS.CustomChillUrl;
            this._customGameResourcesPath = params.GRS.CustomGameResourcesPath;
            this._grsConfiguration = {
                gameState: params.GRS.GameState,
                additionalState: params.GRS.AdditionalState,
                gameTechnology: params.GRS.GameTechnology,
                GRSEnvironment: params.GRS.GRSEnvironment,
                enableVersionSending: params.GRS.enableVersionSending
            };
        }

        this._enableBonusAbusePrevention = params.enableBonusAbusePrevention || false;

        // we need tabID so we don't have multiple games opened in different tabs.
        this._tabID = _.getGuid();
        this._gamePostMessageListener = VoidGamePostMessageListener.create();

        this._subscribe();

    }

    isBonusAbusePreventionEnabled() {
        return this._enableBonusAbusePrevention;
    }

    grsEnvironment() {
        return this._grsConfiguration.GRSEnvironment;
    }

    get isCustomGameResourcesPathUsed() {
        return !!this._customGameResourcesPath;
    }

    get isCustomChillUrlUsed() {
        return !!this._customChillUrl;
    }

    resolveGameImagePath(imageFileName) {
        if(this._resolveGameImagePath) {
            return this._resolveGameImagePath(imageFileName);
        }
        const origin = this.serviceFactory.windowService().getLocationOrigin();

        return `${origin}/gamesImages/${imageFileName}`;
    }

    getBrowserTabId() {
        return this._tabID;
    }


    _subscribe() {
        this.onSystemEvent([SYSTEM_EVENTS.POST_MESSAGE_ARRIVE, SYSTEM_EVENTS.HYBRID_POST_MESSAGE_ARRIVE], (postMessageEvent) => {
            this._gamePostMessageListener.processPostMessage(postMessageEvent.data);
        });
        this.onSystemEvent([SYSTEM_EVENTS.CLOSE_GAME], () => {
            this._gamePostMessageListener.dispose();
            this._gamePostMessageListener = VoidGamePostMessageListener.create();
            this._removeBodyCustomClasses();
            this._currentRunningGameOptions = null;
        }, this);
    }

    openRealGame(gameOptions) {
        let initialGameSequenceBuilder = this._dependenciesFactory.realGameSequenceInitializer().initSequenceBuilder();

        return this._runGameSequence(gameOptions,
            company => company.getRealGameSequenceBuilder(gameOptions, initialGameSequenceBuilder))
            .then(finalGameOptions => {
                this._currentRunningGameOptions = finalGameOptions;
                this._fireGameOpenEvent(gameOptions, true);
                return finalGameOptions;
            })
            .catch(err => {
                this._fireOpenGameErrorEvent(gameOptions, true);
                throw err;
            });

    }

    openAnonymousGame(gameOptions) {
        if (!this.serviceFactory.regulationService().isAnonymousPlayAllowed()) {
            return Promise.reject('Anonymous play is not allowed for the current regulation');
        }

        let initialGameSequenceBuilder = this._dependenciesFactory.anonymousGameSequenceInitializer().initSequenceBuilder();
        return this._runGameSequence(gameOptions,
            company => company.getAnonymousGameSequenceBuilder(gameOptions, initialGameSequenceBuilder))
            .then(finalGameOptions => {
                this._currentRunningGameOptions = finalGameOptions;
                this._fireGameOpenEvent(finalGameOptions, false);
                return finalGameOptions;
            })
            .catch(err => {
                this._fireOpenGameErrorEvent(gameOptions, false);
                throw err;
            });
    }

    openHistoryGame(gameOptions) {
        let initialGameSequenceBuilder = this._dependenciesFactory.historyGameSequenceInitializer().initSequenceBuilder();
        return this._runGameSequence(gameOptions,
            company => company.getHistoryGameSequenceBuilder(gameOptions, initialGameSequenceBuilder))
    }

    _closeDetailHistoryCallback = null;
    attachDetailHistoryCloseHandler(closeDetailHistoryCallback) {
        this._closeDetailHistoryCallback = closeDetailHistoryCallback;
    }

    closeCurrentDetailHistory() {
        if (this._closeDetailHistoryCallback) {
            try {
                this._closeDetailHistoryCallback();
                this._closeDetailHistoryCallback = null;
            } catch (err) {
                this.logger.error('Failed to close detail history widget', err);
            }
        }
    }

    _runGameSequence(initialGameOptions, getGameSequenceBuilderFromCompany) {
        let loadingIndicator = null;

        // When switching from demo to real in the navbar the user can cancel by closing the login form
        // The problem is that loadingIndicator.closeLoadingIndicator() method actually calls Event.fire(SYSTEM_EVENTS.CLOSE_OVERLAY, {priority: PRIORITY_LEVEL.GAME_FRAME});
        // so it will close the game window also
        if (initialGameOptions.inGameModeSwitching) {
            loadingIndicator = VoidLoadingIndicator.create();
        } else {
            loadingIndicator = Device.isHybridEmbedded() ? this._dependenciesFactory.loadingIndicatorEmbedded() : this._dependenciesFactory.loadingIndicator();
        }

        if (this.serviceFactory.hybridService().isDisplayLoadingIndicator(initialGameOptions.gameType)) {
            loadingIndicator.showLoadingIndicator();
        }
        // Add game tab ID to the game step to ensure we don't have multiple games opened in different tabs.
        initialGameOptions.gameTabID = this._tabID;
        initialGameOptions.postMessageHandler = VoidGamePostMessageHandler.create();

        return this._appendGameInfo(initialGameOptions)
            .then(gameOptions => {
                return this._selectCompany(gameOptions.gameInfo.companyName)
                    .then(company => {
                        let sequenceBuilder = getGameSequenceBuilderFromCompany(company);
                        gameOptions.company = company;
                        let steps = sequenceBuilder.build();
                        return this._dependenciesFactory.gameSequenceRunner().run(gameOptions, steps)
                            .then(finalGameOptions => {
                                // we don't need to close loading in case of redirect.
                                // This avoid lobby flicker because it takes time for browser to perform redirect, so user might see lobby for a second
                                if (!gameOptions.isOpenedInRedirect) {
                                    if(finalGameOptions.isOpenedInSecondWebView) {
                                        //in hybrid we show the loading indicator a little longer
                                        // because it takes some time for the second web view to load
                                        DOM.setTimeout(() => this._closeLoadingIndicator(loadingIndicator, finalGameOptions), 3000);
                                    } else {
                                        this._closeLoadingIndicator(loadingIndicator, finalGameOptions)
                                    }
                                }

                                if(finalGameOptions.gameSessionNotificationManager) {
                                    finalGameOptions.gameSessionNotificationManager.startNotifications();
                                }


                                if (finalGameOptions.gamePostMessageListener) {
                                    this._gamePostMessageListener = finalGameOptions.gamePostMessageListener;
                                } else {
                                    this.logger.warn(`Game sequence didn't create any post message listener for game ${gameOptions.gameType}`);
                                    this._gamePostMessageListener = VoidGamePostMessageListener.create();
                                }
                                this._addBodyCustomClasses(finalGameOptions);
                                return finalGameOptions;
                            });
                    });
            }).catch(err => {
                this._closeIndicatorAndFreeResources(loadingIndicator);
                this._showErrorMessage(err,initialGameOptions);

                this.reThrowError('313', 'Run game sequence failed', err, {initialGameOptions});
            });
    }

    _closeIndicatorAndFreeResources(loadingIndicator) {
        try {
            loadingIndicator.closeLoadingIndicator();
            loadingIndicator.shutDown();
        } catch (err) {
            this.logger.warn('_closeIndicatorAndFreeResources Fail.', err);
        }
    }

    _closeLoadingIndicator(loadingIndicator, finalGameOptions) {
        // we call closeLoadingIndicator only if a game window is not already open
        // otherwise closeLoadingIndicator will close it because it fires SYSTEM_EVENTS.CLOSE_OVERLAY
        if(!finalGameOptions.gameWindow || !finalGameOptions.gameWindow.isVisible) {
            this._closeIndicatorAndFreeResources(loadingIndicator);
        }
    }

    _showGenericRestrictionMessage() {
        return RestrictionMessages.create({
            serviceFactory: this.serviceFactory
        }).showGenericMessage();
    }

    _appendGameInfo(gameOptions) {
        return new Promise((resolve, reject) => {
            this.serviceFactory.gamesInformationService().getGamesInfo([gameOptions.gameType])
                .then(gamesInfo => gamesInfo[gameOptions.gameType])
                .then(gameInfo => {
                    if(gameInfo) {
                        resolve(Object.assign(gameOptions, {gameName: gameInfo.title, gameInfo: gameInfo}));
                    } else {
                        this._showGenericRestrictionMessage();
                        reject();
                    }
                });
        });


    }

    _selectCompany(companyName) {
        return this.serviceFactory.companySelectorService().getCompany(companyName);
    }


    _fireGameOpenEvent(gameOptions, isReal) {
        Event.fire(SYSTEM_EVENTS.GAME_OPEN, {
            gametype: gameOptions.gameType, //because in orbit they read it like this
            gameType: gameOptions.gameType, //hoping that they will change it to this
            isReal: isReal
        });
    }

    _fireOpenGameErrorEvent(gameOptions, isReal) {
        Event.fire(SYSTEM_EVENTS.OPEN_GAME_ERROR, {
            gameType: gameOptions.gameType,
            isReal: isReal
        });
    }


    _createCommonReadGameConfigurationRequest(gameOptions) {
        let request = {
            gameTypeId: gameOptions.gameType,
            isSupportFlash: _.isNullOrUndefined(gameOptions.forceMobile) ? Device.isPC() : false,
            isSupportHTML5: Device.isSupportHTML5(),
            isMobile: _.isNullOrUndefined(gameOptions.forceMobile) ? Device.isMobileByOS() : gameOptions.forceMobile,
            GrsData: Store.get('GRSABTesting', ''),
            GameState: this._grsConfiguration.gameState,
            AdditionalState: this._grsConfiguration.additionalState,
            GameTechnology: this._grsConfiguration.gameTechnology,
            regulationId: this.serviceFactory.regulationService().getRegulationCode(),
            currencyCode: this.serviceFactory.currencyService().getCurrency(),
            languageCode: this.serviceFactory.languageService().getISO3(),
            currentRoute: this.serviceFactory.windowService().getPathName(),
            isHybridEmbedded: Device.isHybridEmbedded(),
            productPackage: this.serviceFactory.skinService().getProductPackageID()
        };

        if (!_.isNullOrUndefined(gameOptions.vendorEnvironment)) {
            request.vendorEnvironment = gameOptions.vendorEnvironment;
        }

        if (this._grsConfiguration.GRSEnvironment) {
            request.GrsEnvironment = this._grsConfiguration.GRSEnvironment;
        }

        if (!_.isNullOrUndefined(gameOptions.useNetEntGameInclusion)) {
            request.useNetEntGameInclusion = gameOptions.useNetEntGameInclusion;
        }

        if (!_.isNullOrUndefined(gameOptions.useEvolutionNewIntegration)) {
            request.useEvolutionNewIntegration = gameOptions.useEvolutionNewIntegration;
        }

        if (!_.isNullOrUndefined(gameOptions.useAdapterIntegration)) {
            request.useAdapter = gameOptions.useAdapterIntegration;
        }

        if (false !== this._grsConfiguration.enableVersionSending) {
            let gameVersion = this.serviceFactory.hybridService().getCompiledGameVersion(gameOptions.gameType);
            if (gameVersion) { // better validation
                request.GameState = gameVersion;
            }
        }

        if (gameOptions.partyJackpot && gameOptions.partyJackpot.jackpotId) {
            request.partyJackpotId = gameOptions.partyJackpot.jackpotId;
        }

        return request;
    }

    _handleGameConfigurationResult(gameConfigurationFromServer) {
        if(gameConfigurationFromServer.grsData) {
            let customChillUrl = this._customChillUrl;
            if(customChillUrl){
                gameConfigurationFromServer.grsData.chillUrl = customChillUrl;
            }

            if(this._customGameResourcesPath){
                gameConfigurationFromServer.grsData.customGameResourcesPath = this._customGameResourcesPath;
            }

            Store.set('GRSABTesting', gameConfigurationFromServer.grsData.abTestingData)
        }

        if(gameConfigurationFromServer.sessionTimer) {
            gameConfigurationFromServer.sessionTimer = TimeSpan.parse(gameConfigurationFromServer.sessionTimer);
        }

        return gameConfigurationFromServer;
    }

    readRealGameConfiguration(gameOptions) {

        let requestParams = Object.assign(this._createCommonReadGameConfigurationRequest(gameOptions), {
            refreshToken: this.serviceFactory.oAuth2Service().getRefreshToken()
        });

        if(gameOptions.realityCheckInterval) {
            requestParams.realityCheckIntervalInMinutes = Math.round(gameOptions.realityCheckInterval.timeSpan.totalMinutes);
        }

        if(gameOptions.openSession) {
            requestParams.reconnectionGameSessionId = gameOptions.openSession.SessionID;
        }

        if(gameOptions.italy) {
            requestParams.requestedGameLimitAmount = gameOptions.italy.gameLimits;
        }

        if(gameOptions.spain) {
            requestParams.requestedGameLimitAmount = gameOptions.spain.gameLimits.RequestedGameLimit;
            requestParams.requestedTimeLimitInMinutes = gameOptions.spain.gameLimits.RequestedTimeLimit;
            requestParams.restrictionPeriodInMinutes = gameOptions.spain.gameLimits.RestrictionPeriod;
            requestParams.intervalReminderInMinutes = gameOptions.spain.gameLimits.IntervalReminderInMinutes;

        }

        return this.serviceFactory.http2Service()
            .post(this._openRealGameApiUrl, requestParams)
            .then(gameConfiguration => this._handleGameConfigurationResult(gameConfiguration));
    }

    readAnonymousGameConfiguration(gameOptions) {
        let requestParams = this._createCommonReadGameConfigurationRequest(gameOptions);

        return this.serviceFactory.http2Service()
            .post(this._openAnonymousGameApiUrl, requestParams)
            .then(gameConfiguration => this._handleGameConfigurationResult(gameConfiguration));
    }


    readHistoryGameConfiguration(gameOptions) {
        let requestParams = Object.assign(this._createCommonReadGameConfigurationRequest(gameOptions), {
            gameRoundId: gameOptions.gameRoundId,
            gameRoundDate: gameOptions.gameRoundDate,
            refreshToken: this.serviceFactory.oAuth2Service().getRefreshToken()
        });

        return this.serviceFactory.http2Service()
            .post(this._openHistoryGameApiUrl, requestParams)
            .then(gameConfiguration => this._handleGameConfigurationResult(gameConfiguration));
    }

    /**
     * @description Return the game background images path
     * @param gameBackgroundImage the full background image
     *
     */
    loadGameBackground(gameBackgroundImage) {
        return imagePreloadWithDefault(gameBackgroundImage, this._backgroundDefault);
    }

    getGameSessionData(gameType) {
        return this.serviceFactory.http2Service().post(this._getGameSessionDataUrl, {
            gameTypeId: gameType
        });
    }

    /**
     * returns TimeSpan from interval or TimeSpan from 5 minutes
     * @param interval
     * @returns {TimeSpan}
     * @private
     */
    _setAffiliatesGoToLobbyInterval(interval){
        if (interval){
            return TimeSpan.parse(interval);
        } else {
            return TimeSpan.fromMinutes(5);
        }
    }

    getAffiliatesGoToLobbyInterval() {
        return this._affiliatesGoToLobbyInterval;
    }

    getChillEnvironment() {
        return this._chillEnvironment;
    }

    getGameDefaultIcon() {
        return this._gameDefaultIcon;
    }

    getGRSGameTechnology() {
        if(this._grsConfiguration) {
            return this._grsConfiguration.gameTechnology;
        }

        return null;
    }

    getGRSGameState(){
        if(this._grsConfiguration) {
            return this._grsConfiguration.gameState;
        }

        return null;
    }

    getGRSAdditionalState() {
        if(this._grsConfiguration) {
            return this._grsConfiguration.additionalState;
        }
        return null;
    }

    _IsIgnoreError(err) {
        try {
            if (isCancelGameFlowResponse(err)) {
                return true;
            }

            //todo should be refactored to used the isCancelGameFlowResponse
            if (err.toString().includes(ON_DEMAND.GAME_LOADING_CLOSE_BY_X)) {
                return true;
            }
        } catch (e) {
            this.logger.error('Error in _IsIgnoreError', err);
        }

        return false;
    }

    _showErrorMessage(err, gameOptions) {
        if (this._IsIgnoreError(err)) {
            return;
        }

        let messageText = null;
        if (err.error_code) {
            messageText = this.serviceFactory.regulationService().getErrorMessage(err.error_code);
        }

        if (!messageText) {
            messageText = this.serviceFactory.regulationService().getErrorMessage(CGP_ERROR_CODES.GENERAL_ERROR);
        }

        this.serviceFactory.messageManagerService().openMessage({
            text: messageText,
            type: 'error',
            closeButton: false,
            hasDefaultButton: false,
            buttons: [
                {
                    name: this.serviceFactory.languageService().translation('mdp_ok'),
                    callback: () => {
                        Event.fire(SYSTEM_EVENTS.ANALYTIC_TRIGGER_ACTIVATED, {category: err.error_code, action: 'acknowledge'});
                    },
                    isClosable: true
                }
            ]
        });
        this._fireGameLoadingEventFailed(err,gameOptions);
    }

    closeRunningGame() {
        if(this._currentRunningGameOptions && this._currentRunningGameOptions.gameWindow) {
            this._currentRunningGameOptions.gameWindow.closeGameWindow();
        } else{
            if (Device.isHybridEmbedded() && this.isGameRunning()) {
                Event.fire(SYSTEM_EVENTS.SECOND_WEB_VIEW_CLOSE_ALL);
            }
            this._currentRunningGameOptions = null;
        }
    }

    isGameRunning() {
        return !_.isNullOrUndefined(this._currentRunningGameOptions);
    }

    isGameRunningInFreePlayMode() {
        if (!this.isGameRunning()) {
            return false;
        }

        if (!_.isNullOrUndefined(this._currentRunningGameOptions)) {
            return _.isObject(this._currentRunningGameOptions.freePlayData);
        }

        return false;
    }


    getCurrentRunningGamecompanyName() {
        if(this._currentRunningGameOptions) {
            return this._currentRunningGameOptions.gameInfo.companyName;
        }

        return null;
    }

    getCurrentRunningGameCompany() {
        if(this._currentRunningGameOptions) {
            return this._currentRunningGameOptions.company;
        }

        return null;
    }

    stopCurrentGameAutoplay() {
        if(this._currentRunningGameOptions) {
            this.getCurrentRunningGameCompany().stopGameAutoPlay(this._currentRunningGameOptions);
        }
    }

    continuePlayCurrentGame() {
        if(this._currentRunningGameOptions) {
            this.getCurrentRunningGameCompany().continueGamePlay(this._currentRunningGameOptions);
        }
    }

    getCustomGameUrl(gameType) {
        if (!gameType) {
            this.throwArgumentExpected('674', 'getCustomGameUrl', 'gameType');
        }

        return this._customGamesSettings[gameType] && this._customGamesSettings[gameType].customUrl;
    }

    getGRSEnvironments(){
        return this.serviceFactory.http2Service().get(this._getGRSEnvironmentsUrl);
    }

    _addBodyCustomClasses(gameOptions) {
        const company = gameOptions ? gameOptions.company : null;

        if (!company) {
            return;
        }

        const classNames = company.getCustomBodyClasses(gameOptions);
        DOM.addClass(DOM.getBody(), classNames);
    }

    _removeBodyCustomClasses() {

        const company = this._currentRunningGameOptions ? this._currentRunningGameOptions.company : null;

        if (!company) {
            return;
        }

        const classNames = company.getCustomBodyClasses(this._currentRunningGameOptions);

        if (_.isArray(classNames) && classNames.length > 0) {
            classNames.forEach(className => DOM.removeClass(DOM.getBody(), className));
        }
    }

    _fireGameLoadingEventFailed(err, gameOptions){

        let errMsgToLog =  err;
        if(_.isObject(err)){
            errMsgToLog = JSON.stringify(errMsgToLog);
        }else{
            errMsgToLog = err && _.isString(err) && err.toString();
        }
        Event.fire(SYSTEM_EVENTS.ANALYTIC_TRIGGER_ACTIVATED, {category: 'game_load_error', action: errMsgToLog, label:gameOptions.gameType});
    }

    isCurrentUrlAnOpenGameRoute() {
        return this.serviceFactory.windowService().getHash().startsWith('#page/game/');
    }
}